import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface BalSaeDaeProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

function BalSaeDaeMovesBody() {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">JOONBI</h4>
        <p className="text-gray-700">
          Right hand clench with left hand c cupped Chamber hands to left whilst twisting, skip forward with stamp into x stance right foot lead
          delivering right back fist to nose with rear hand guarding block.
        </p>
      </div>
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">MOVES</h4>
        <ol className="list-none space-y-2">
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">1.</span>
            <span className="text-gray-700">
              Turn 180 to rear whilst, Landing in left front stance with lead hand inner forearm outer block, rear inner outer forearm block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">2.</span>
            <span className="text-gray-700">
              Turn 180 into front stance right leg forward with rear inner hammer block, lead inner forearm outer block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">3.</span>
            <span className="text-gray-700">
              Turn 90 to right lifting right leg into a groin guard with right arm glancing to knee, before landing in right front stance delivering
              right inner forearm outer block followed by rear inner forearm outer block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">4.</span>
            <span className="text-gray-700">
              Adjust right foot, turn to front in horse stance with mid-section left hooking punch (KeumGang). Left knife hand strike to neck, right inner
              forearm out block, left inner forearm outer block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">5.</span>
            <span className="text-gray-700">
              Swan step left and step right foot forward into back stance with knife hand guarding block Step forward left foot into back stance with
              knife hand guarding block, step forward right foot into back stance with knife hand guarding block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">6.</span>
            <span className="text-gray-700">
              Step right leg back into back stance with knife hand guarding block Adjust into tiger stance with rear ridge hand to lead hand palm Rear
              leg side kick (<span className="font-semibold">KIHAP</span>) landing facing rear in back stance with knife hand guarding block Step
              forward into back-stance with knife hand guarding block
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">7.</span>
            <span className="text-gray-700">
              Pull back into cat stance with low crane hands, wedging block, pull back chambering hands to waist, adjust into right front stance with
              twin upset punch to ribs. Step together and right foot out into front stance with rear hand parry and front hand mid punch.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">8.</span>
            <span className="text-gray-700">
              Turn 180 (dora) into front stance with rear arm groin strike (think Koryo) pull back to spreading block, rear crescent kick to front
              landing in horse stance (right leg to front) with right down block, left reverse ridge to rear, right rear crescent kick to hand landing
              in horse stance with right elbow to hand at solar plexus, 9 and 9 block (right down block with left hand to right shoulder).
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">9.</span>
            <span className="text-gray-700">
              Draw right foot in to left and turn out to right (to rear) in front stance with u shaped punch, pull back Re chamber hands to left /
              right (like Rohai), left leg crescent kick landing in front stance with u shaped punch, draw back Re chamber hands right / left, right
              leg crescent kick landing front stance with u shaped punch.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">10.</span>
            <span className="text-gray-700">
              Back-end round facing into deep front stance with left leg lead and right inner forearm outer block, adjust to opposite side and repeat
              Step left leg up to swan step through at 45, right step through to back stance with knife hand guarding block.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">11.</span>
            <span className="text-gray-700">
              Repeat to opposite side: Step right foot to left side at 45, then left steps to finish in back stance knife hand guarding block.
            </span>
          </li>
        </ol>
      </div>
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">CLOSING</h4>
        <p className="text-gray-700">Joonbi</p>
      </div>
    </div>
  );
}

export default function BalSaeDae({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: BalSaeDaeProps) {
  const isVisible = ["BLACK/WHITE", "BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt);

  if (!isVisible) return null;

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["balsaekdae", "balsaek_dae", "bal_saek_dae"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["balsaekdae", "balsaek_dae", "bal_saek_dae"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">Storming the enemy fortress (Big)</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="BalSaek Dae">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="key-elements" className={triggerClass}>
              Key Elements
            </TabsTrigger>
            <TabsTrigger value="requirements" className={triggerClass}>
              Requirements
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="text-gray-700">
                BalSaekDae is a high-level black belt pattern that represents the pinnacle of foot techniques and advanced movements. This pattern
                emphasizes the integration of complex footwork with powerful striking techniques.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-elements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Elements</h4>
              <ul className="list-inside list-disc space-y-2 text-gray-700">
                <li>Advanced footwork patterns</li>
                <li>Complex kicking combinations</li>
                <li>Swift directional changes</li>
                <li>Integration of hand and foot techniques</li>
                <li>Advanced balance and control</li>
                <li>Strategic movement and positioning</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="requirements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Requirements</h4>
              <p className="text-gray-700">
                Black belt practitioners must demonstrate exceptional control and understanding of the pattern&apos;s advanced techniques. Focus is
                placed on the seamless integration of movements, maintaining proper form throughout complex sequences, and displaying both power and
                precision in execution.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <BalSaeDaeMovesBody />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Description</h4>
          <p className="text-gray-700">
            BalSaekDae is a high-level black belt pattern that represents the pinnacle of foot techniques and advanced movements. This pattern
            emphasizes the integration of complex footwork with powerful striking techniques.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Elements</h4>
          <ul className="list-inside list-disc space-y-2 text-gray-700">
            <li>Advanced footwork patterns</li>
            <li>Complex kicking combinations</li>
            <li>Swift directional changes</li>
            <li>Integration of hand and foot techniques</li>
            <li>Advanced balance and control</li>
            <li>Strategic movement and positioning</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Requirements</h4>
          <p className="text-gray-700">
            Black belt practitioners must demonstrate exceptional control and understanding of the pattern&apos;s advanced techniques. Focus is placed
            on the seamless integration of movements, maintaining proper form throughout complex sequences, and displaying both power and precision in
            execution.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
          <BalSaeDaeMovesBody />
        </div>
      </div>
    </div>
  );
}
